import { useState, useEffect, useMemo } from 'react';
import { Box, MenuItem } from '@mui/material';
import { Control, FieldValues, Path, useController, useWatch } from 'react-hook-form';
import { getMaterials } from '@/shared/api/materialsService';
import { Material } from '@/shared/types/material';
import { FormSelect } from './FormSelect';

interface MaterialAutocompleteFieldProps<T extends FieldValues> {
  categoryName: Path<T>;
  subcategoryName: Path<T>;
  materialIdName: Path<T>;
  control: Control<T>;
  categoryLabel: string;
  subcategoryLabel: string;
  categoryPlaceholder?: string;
  subcategoryPlaceholder?: string;
  required?: boolean;
  categoryError?: boolean;
  categoryHelperText?: string;
  subcategoryError?: boolean;
  subcategoryHelperText?: string;
  requiredMessage?: string;
  subcategoryRequiredMessage?: string;
  disabled?: boolean;
  categoryId?: string;
  subcategoryId?: string;
  categoryAriaLabel?: string;
  subcategoryAriaLabel?: string;
}

/**
 * Reusable material selector rendered as two dropdowns:
 * 1) category
 * 2) subcategory (dependent on selected category)
 *
 * The component also keeps the hidden materialId field in sync from the selected pair.
 */
export function MaterialAutocompleteField<T extends FieldValues>({
  categoryName,
  subcategoryName,
  materialIdName,
  control,
  categoryLabel,
  subcategoryLabel,
  categoryPlaceholder = '',
  subcategoryPlaceholder = '',
  required = false,
  categoryError = false,
  categoryHelperText,
  subcategoryError = false,
  subcategoryHelperText,
  requiredMessage,
  subcategoryRequiredMessage,
  disabled = false,
  categoryId,
  subcategoryId,
  categoryAriaLabel,
  subcategoryAriaLabel,
}: Readonly<MaterialAutocompleteFieldProps<T>>) {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedCategory = useWatch({ control, name: categoryName }) as string;
  const selectedSubcategory = useWatch({ control, name: subcategoryName }) as string;
  const { field: categoryField } = useController({ control, name: categoryName });
  const { field: subcategoryField, fieldState: subcategoryFieldState } = useController({
    control,
    name: subcategoryName,
    rules: {
      required: required ? subcategoryRequiredMessage || requiredMessage || 'Ce champ est obligatoire' : false,
    },
  });
  const { field: materialIdField } = useController({ control, name: materialIdName });

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const data = await getMaterials();
        setMaterials(data);
      } catch (error) {
        console.error('Error fetching materials:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchMaterials();
  }, []);

  const categoryOrder: Record<string, number> = {
    MATERIAUX: 1,
    MATERIELS: 2,
    'FOURNITURES VRD': 3,
    'FOURNITURES BÂTIMENT': 4,
  };

  const categories = useMemo(() => {
    const unique = Array.from(new Set(materials.map(material => material.category)));
    return unique.sort((a, b) => {
      const orderA = categoryOrder[a] || 999;
      const orderB = categoryOrder[b] || 999;
      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    });
  }, [materials]);

  const formatDisplayLabel = (value: string): string => {
    return value
      .toLowerCase()
      .replace(/(^|[\s'-])([a-z\u00c0-\u00ff])/g, (_, sep: string, char: string) => {
        return `${sep}${char.toUpperCase()}`;
      });
  };

  const subcategories = useMemo(
    () =>
      materials
        .filter(material => material.category === selectedCategory)
        .sort((a, b) => a.subcategory.localeCompare(b.subcategory)),
    [materials, selectedCategory]
  );

  useEffect(() => {
    if (loading || !materials.length) return;
    if (selectedCategory && selectedSubcategory) return;
    if (!materialIdField.value) return;

    const matchedMaterial = materials.find(material => material.id === materialIdField.value);
    if (!matchedMaterial) return;

    if (!selectedCategory) {
      categoryField.onChange(matchedMaterial.category);
    }
    if (!selectedSubcategory) {
      subcategoryField.onChange(matchedMaterial.subcategory);
    }
  }, [
    loading,
    materials,
    selectedCategory,
    selectedSubcategory,
    materialIdField.value,
    categoryField,
    subcategoryField,
  ]);

  return (
    <Box sx={{ display: 'grid', gap: 2, width: '100%', minWidth: 0 }}>
      <FormSelect
        sx={{ minWidth: 0 }}
        id={categoryId}
        value={categoryField.value || ''}
        onChange={event => {
          const newCategory = event.target.value as string;
          categoryField.onChange(newCategory);
          subcategoryField.onChange('');
          materialIdField.onChange('');
        }}
        label={categoryLabel}
        required={required}
        error={categoryError}
        helperText={categoryHelperText}
        disabled={disabled || loading}
        aria-label={categoryAriaLabel || categoryLabel}
      >
        <MenuItem value="" disabled>
          {loading ? 'Chargement...' : categoryPlaceholder}
        </MenuItem>
        {categories.map(category => (
          <MenuItem key={category} value={category}>
            {formatDisplayLabel(category)}
          </MenuItem>
        ))}
      </FormSelect>

      <FormSelect
        sx={{ minWidth: 0 }}
        id={subcategoryId}
        value={subcategoryField.value || ''}
        onChange={event => {
          const newSubcategory = event.target.value as string;
          subcategoryField.onChange(newSubcategory);

          const matchedMaterial = materials.find(
            material => material.category === selectedCategory && material.subcategory === newSubcategory
          );

          materialIdField.onChange(matchedMaterial?.id || '');
        }}
        label={subcategoryLabel}
        required={required}
        error={subcategoryError || !!subcategoryFieldState.error}
        helperText={subcategoryHelperText || subcategoryFieldState.error?.message}
        disabled={disabled || loading || !selectedCategory}
        aria-label={subcategoryAriaLabel || subcategoryLabel}
      >
        <MenuItem value="" disabled>
          {loading
            ? 'Chargement...'
            : selectedCategory
              ? subcategoryPlaceholder
              : categoryPlaceholder}
        </MenuItem>
        {subcategories.map(material => (
          <MenuItem key={material.id} value={material.subcategory}>
            {formatDisplayLabel(material.subcategory)}
          </MenuItem>
        ))}
      </FormSelect>
    </Box>
  );
}
